import { useCallback } from "react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import useTypedContext from "hooks/useTypedContext";
import useTaskCreate from "shared/Stack/useTaskCreate";
import Tooltip from "ds/components/Tooltip";

import { useRunShortcut } from "../../hooks/useRunShortcut";

type PerformTaskAgainButtonProps = {
  command: string | null;
  stackId: string;
  errorMessage?: string;
};

const PerformTaskAgainButton = ({
  command,
  stackId,
  errorMessage,
}: PerformTaskAgainButtonProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);
  const navigate = useNavigate();

  const [createTask, { loading, data }] = useTaskCreate({
    refetchQueries: ["GetRun"],
  });

  const isLoading = loading || !!data?.taskCreate?.id;

  const performTaskAgain = useCallback(() => {
    createTask({ stack: stackId, command: command || "", skipInitialization: false }, (data) => {
      if (data?.taskCreate?.id) {
        const taskId = data.taskCreate.id;
        reportSuccess({ message: `Task ${taskId} successfully triggered` });
        navigate(`/stack/${stackId}/run/${taskId}`);
      }
    });
  }, [createTask, stackId, command, reportSuccess, navigate]);

  useRunShortcut({
    label: "Perform again",
    code: "mod+alt+p",
    callback: performTaskAgain,
    loading: isLoading,
  });

  return (
    <Tooltip
      on={(props) => (
        <Button
          {...props}
          variant="secondary"
          loading={isLoading}
          disabled={isLoading || !!errorMessage}
          onPress={performTaskAgain}
          size="small"
        >
          Perform again
        </Button>
      )}
      active={!!errorMessage}
    >
      {errorMessage}
    </Tooltip>
  );
};

export default PerformTaskAgainButton;
